/**
 * @file Page: Title
 * @description The application's entry point, showing the game title and main navigation options.
 */
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/atoms/Button';
import SoundControlButton from '../../components/molecules/SoundControlButton';
import OptionsModal from '../../components/organisms/OptionsModal';
import { useBackgroundImage } from '../../contexts/BackgroundImageContext';
import { useCaseDiscoverySetup } from '../../hooks/useCaseDiscoverySetup';
import { useGameConfig } from '../../hooks/useGameConfig';
import { useSeasonUnlock } from '../../hooks/useSeasonUnlock';
import { getAssetPath } from '../../utils/assetPath';
import { shouldShowFTUEVideo } from '../../utils/ftueStorage';

import './TitlePage.css';

const backgroundImage = getAssetPath('/assets/images/home-background.png');

const TitlePage: React.FC = () => {
  const { visibleCases, seasons, loading, error } = useCaseDiscoverySetup();
  const { seasonUnlockInfos, isLevelCompleted } = useSeasonUnlock(
    visibleCases,
    seasons,
  );
  const { gameConfig } = useGameConfig();
  const navigate = useNavigate();
  const [bgLoaded, setBgLoaded] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const { setBackgroundImageUrl } = useBackgroundImage();

  // Note: useCaseDiscoverySetup already starts title music on mount

  // Compute target episode for PLAY button (also used for background video)
  const targetEpisode = useMemo(() => {
    const allPlayableEpisodes = seasonUnlockInfos.flatMap((s) =>
      s.episodes.filter((e) => e.isPlayable),
    );

    if (allPlayableEpisodes.length === 0) {
      return null;
    }

    // Find first unplayed episode (not completed by user)
    const firstUnplayedEpisode = allPlayableEpisodes.find(
      (e) => !isLevelCompleted(e.caseInfo.caseId),
    );

    // If all episodes completed, return the last (most recent) playable episode
    return (
      firstUnplayedEpisode ||
      allPlayableEpisodes[allPlayableEpisodes.length - 1]
    );
  }, [seasonUnlockInfos, isLevelCompleted]);

  // Check if fonts are loaded
  useEffect(() => {
    // Check if document.fonts API is available
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
    } else {
      // Fallback for browsers without document.fonts API
      setTimeout(() => setFontsLoaded(true), 100);
    }
  }, []);

  // Check if background image loaded successfully
  useEffect(() => {
    const img = new Image();
    img.src = backgroundImage;
    img.onload = () => {
      setBgLoaded(true);
      setBackgroundImageUrl(backgroundImage);
    };
    img.onerror = (e) => {
      console.error('Background image failed to load:', e);
      setBgLoaded(false);
    };

    // Cleanup: clear background when component unmounts
    return () => {
      setBackgroundImageUrl(null);
    };
  }, [setBackgroundImageUrl]);

  // Combine loading states to determine when everything is ready
  useEffect(() => {
    if (bgLoaded && fontsLoaded) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        setIsReady(true);
      }, 50);
    }
  }, [bgLoaded, fontsLoaded]);

  const handlePlayFirstCase = () => {
    if (!targetEpisode) {
      alert('No playable cases available.');
      return;
    }

    // Check if FTUE video should be shown
    if (gameConfig && shouldShowFTUEVideo(gameConfig)) {
      navigate('/ftue');
    } else {
      navigate(`/play/${targetEpisode.caseInfo.caseId}`);
    }
  };

  const handleCaseLibrary = () => {
    navigate('/cases');
  };

  // Render content based on loading/error state
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center px-[53px] pt-[20px] z-10">
          <p
            className="font-lato font-normal text-[16px] leading-[1.3] text-center text-white"
            style={{ textShadow: 'rgba(8,13,24,0.4) 0px 0px 10px' }}
          >
            Loading cases...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center px-[53px] pt-[20px] z-10">
          <p
            className="font-lato font-normal text-[16px] leading-[1.3] text-center text-red-500"
            style={{ textShadow: 'rgba(8,13,24,0.4) 0px 0px 10px' }}
          >
            {error}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col items-center justify-center px-[53px] pt-[20px] z-10">
          <p
            className="font-lato font-normal text-[16px] leading-[1.3] text-center text-white text-shadow-lg"
            style={{
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
            }}
          >
            Uncover clues to reveal the deeper corruption plaguing the city of
            Caldera.
          </p>
        </div>

        <div className="flex-1" />

        <div className="flex flex-col gap-[20px] items-center pb-[44px] px-[32px] z-10">
          <Button
            onClick={handlePlayFirstCase}
            disabled={
              seasonUnlockInfos
                .flatMap((s) => s.episodes)
                .filter((e) => e.isPlayable).length === 0
            }
            variant="title-play"
            size="title"
          >
            <p
              className="font-oswald font-medium text-[88px]/[normal] tracking-[1.6px]"
              style={{ textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)' }}
            >
              PLAY
            </p>
          </Button>
          <Button
            onClick={handleCaseLibrary}
            variant="title-more-cases"
            size="title"
          >
            <p
              className="font-oswald text-[32px]/[normal] tracking-[1.6px]"
              style={{
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
              }}
            >
              ALL EPISODES
            </p>
          </Button>
        </div>
      </>
    );
  };

  // Main render
  return (
    <div
      className={`bg-center bg-cover bg-no-repeat relative size-full transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'} flex flex-col`}
      style={{
        backgroundImage:
          bgLoaded &&
          !targetEpisode?.caseInfo.titleVideoUrl &&
          !gameConfig?.titleVideo
            ? `url(${backgroundImage})`
            : 'none',
        backgroundColor: '#1a1a2e',
      }}
    >
      {/* Background video: prioritize target episode's video, fallback to gameConfig */}
      {(targetEpisode?.caseInfo.titleVideoUrl || gameConfig?.titleVideo) && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src={
            targetEpisode?.caseInfo.titleVideoUrl || gameConfig?.titleVideo?.url
          }
        />
      )}
      {/* Top gradient overlay */}
      <div
        className="absolute inset-x-0 top-0 h-[300px] pointer-events-none z-1"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0))',
        }}
      />
      {/* Bottom gradient overlay */}
      <div
        className="absolute inset-x-0 bottom-0 h-[400px] pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000)',
        }}
      />
      {/* Sound Control Button and Panel */}
      <SoundControlButton onClick={() => setIsOptionsModalOpen(true)} />
      {/* Options Modal */}
      <OptionsModal
        isOpen={isOptionsModalOpen}
        onClose={() => setIsOptionsModalOpen(false)}
      />
      {/* Title Section */}
      <div className="flex flex-col items-center justify-center px-[32px] z-10 text-shadow-lg">
        <h1
          className="font-oswald text-[88px] leading-[1.1] tracking-[4.4px] text-center text-white mb-0"
          style={{
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
          }}
        >
          CLUE HUNTER
        </h1>
      </div>
      {/* Dynamic Content Section */}
      {renderContent()}
    </div>
  );
};

export default TitlePage;
